import { useEffect, useMemo, useState } from "react";
import PageBreadcrumbs from "../../layouts/PageBreadcrumbs.js";
import { SkeletonTable } from "../../components/Skeleton.js";
import { Link } from "react-router-dom";
import { api, ApiError, DhisAppClient, TenantWorkspace } from "../../lib/api.js";
import { useTableState } from "../../lib/useTableState.js";
import TableToolbar from "../../components/TableToolbar.js";

type CreateForm = {
  workspaceId: string;
  label: string;
  description: string;
  dhis2BaseUrl: string;
  allowedAuthorities: string;
};

const EMPTY_CREATE_FORM: CreateForm = {
  workspaceId: "",
  label: "",
  description: "",
  dhis2BaseUrl: "",
  allowedAuthorities: ""
};

function statusPill(status?: DhisAppClient["status"]) {
  if (status === "active") return <span className="pill pill-ok">Active</span>;
  if (status === "revoked") return <span className="pill pill-err">Revoked</span>;
  return <span className="pill pill-muted">{status ?? "—"}</span>;
}

function fmt(date?: string): string {
  return date ? new Date(date).toLocaleString() : "—";
}

export default function AdminAppClients() {
  const [clients, setClients] = useState<DhisAppClient[]>([]);
  const [tenantWorkspaces, setTenantWorkspaces] = useState<TenantWorkspace[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "revoked">("all");
  const [workspaceFilter, setWorkspaceFilter] = useState<string>("all");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [revealedSecret, setRevealedSecret] = useState<{ clientId: string; secret: string } | null>(null);
  const [createForm, setCreateForm] = useState<CreateForm | null>(null);
  const [creating, setCreating] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const [{ clients }, ws] = await Promise.all([
        api.listAppClients(),
        api.workspace().catch(() => ({ workspaces: [] as TenantWorkspace[] }))
      ]);
      setClients([...(clients ?? [])].sort((a, b) => (b.updatedAt ?? "").localeCompare(a.updatedAt ?? "")));
      setTenantWorkspaces(ws.workspaces ?? []);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void refresh(); }, []);

  async function submitCreate() {
    if (!createForm) return;
    if (!createForm.workspaceId) {
      setError("Pick a target workspace.");
      return;
    }
    setCreating(true);
    setError(null);
    try {
      const r = await api.createAppClient({
        workspaceId: createForm.workspaceId,
        label: createForm.label.trim(),
        description: createForm.description.trim() || undefined,
        dhis2BaseUrl: createForm.dhis2BaseUrl.trim() || undefined,
        allowedAuthorities: createForm.allowedAuthorities
          .split(",").map((s) => s.trim()).filter(Boolean)
      });
      setRevealedSecret({ clientId: r.client.clientId, secret: r.clientSecret });
      setBanner(`Created app client ${r.client.clientId} — copy the secret below; it won't be shown again.`);
      setCreateForm(null);
      await refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setCreating(false);
    }
  }

  const workspaces = useMemo(
    () => Array.from(new Set(clients.map((c) => c.workspaceId || "—"))).sort(),
    [clients]
  );

  const filtered = useMemo(() => {
    return clients.filter((c) => {
      if (statusFilter !== "all" && c.status !== statusFilter) return false;
      if (workspaceFilter !== "all" && (c.workspaceId || "—") !== workspaceFilter) return false;
      return true;
    });
  }, [clients, statusFilter, workspaceFilter]);

  const t = useTableState(filtered, {
    searchFields: ["label", "clientId", "workspaceId", "dhis2BaseUrl", "status"],
    initialPageSize: 25
  });

  const onRotate = async (c: DhisAppClient) => {
    if (!confirm(`Rotate the secret for "${c.label}"? The previous secret will stop working immediately.`)) return;
    setBusyId(c.clientId);
    try {
      const { client, clientSecret } = await api.rotateAppClientSecret(c.clientId);
      setRevealedSecret({ clientId: client.clientId, secret: clientSecret });
      await refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  };

  const onRevoke = async (c: DhisAppClient) => {
    if (!confirm(`Revoke "${c.label}"? It will no longer be usable.`)) return;
    setBusyId(c.clientId);
    try {
      await api.revokeAppClient(c.clientId);
      await refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  };

  // v1.17.0 — queue a "run full backfill" command for this client. The
  // DHIS2 app picks it up on its next heartbeat (≤60s) and pushes the
  // entire historical metadata + data corpus to the platform.
  const onTriggerBackfill = async (c: DhisAppClient) => {
    if (!confirm(
      `Trigger a full historical backfill on "${c.label}"?\n\n`
      + `The next time the DHIS2 app phones home (within 60s), it will fetch every `
      + `metadata object, every tracker event, and every aggregate data value from the `
      + `paired DHIS2 instance and push it to the platform. Existing platform data is `
      + `not deleted; the ingest is idempotent so duplicates are de-duped server-side.`
    )) return;
    setBusyId(c.clientId);
    try {
      await api.triggerAppClientBackfill(c.clientId, "Operator-triggered from /admin/administration/dhis2/app-clients");
      setError(null);
      // No data refresh needed — pending-command state is shown via a
      // future fetch of /commands; for now the confirm dialog tells the
      // operator the command was queued.
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  };

  const activeCount = clients.filter((c) => c.status === "active").length;
  const revokedCount = clients.filter((c) => c.status === "revoked").length;

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>DHIS2 app clients</h1>
          <p>Platform-issued credentials used by the embedded TallyStream DHIS2 app to call the platform API.</p>
        </div>
        <div className="actions">
          <button type="button" className="btn btn-primary" onClick={() => setCreateForm({ ...EMPTY_CREATE_FORM })}>
            + Add app client
          </button>
          <button className="btn" onClick={() => void refresh()} disabled={loading}>Refresh</button>
        </div>
      </header>

      {error && <div className="alert alert-error">{error}</div>}
      {banner && <div className="ts-alert ts-alert-info">{banner}</div>}

      <section className="section">
        <div className="stat-grid">
          <div className="stat"><div className="stat-label">Clients</div><div className="stat-value">{clients.length}</div></div>
          <div className="stat"><div className="stat-label">Active</div><div className="stat-value">{activeCount}</div></div>
          <div className="stat"><div className="stat-label">Revoked</div><div className="stat-value">{revokedCount}</div></div>
          <div className="stat"><div className="stat-label">Workspaces with clients</div><div className="stat-value">{workspaces.length}</div></div>
        </div>
      </section>

      {revealedSecret && (
        <div className="alert alert-warning" style={{ marginBottom: 12 }}>
          <strong>New secret for client <code>{revealedSecret.clientId}</code> — copy it now, it won't be shown again:</strong>
          <pre style={{ whiteSpace: "pre-wrap", marginTop: 8 }}>{revealedSecret.secret}</pre>
          <button className="linkbtn" onClick={() => setRevealedSecret(null)}>Dismiss</button>
        </div>
      )}

      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
        <label className="muted" style={{ fontSize: 12 }}>Status</label>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}>
          <option value="all">All</option>
          <option value="active">Active</option>
          <option value="revoked">Revoked</option>
        </select>
        <label className="muted" style={{ fontSize: 12, marginLeft: 12 }}>Workspace</label>
        <select value={workspaceFilter} onChange={(e) => setWorkspaceFilter(e.target.value)}>
          <option value="all">All workspaces ({workspaces.length})</option>
          {workspaces.map((w) => <option key={w} value={w}>{w}</option>)}
        </select>
      </div>

      {loading && clients.length === 0 ? (
        <SkeletonTable rows={5} columns={4} />
      ) : filtered.length === 0 ? (
        <div className="empty">
          <p><strong>No DHIS2 app clients match the filters.</strong></p>
          <p className="muted">
            App clients are issued per workspace and typically created via the discovery flow under{" "}
            <Link to="/admin/instances">DHIS2 instances</Link>.
          </p>
        </div>
      ) : (
        <div className="table-scroll">
          <TableToolbar
            hideExport
            search={t.search}
            setSearch={t.setSearch}
            page={t.page}
            setPage={t.setPage}
            pageSize={t.pageSize}
            setPageSize={t.setPageSize}
            totalPages={t.totalPages}
            totalRows={t.totalRows}
            searchPlaceholder="Search app clients…"
          />
          <table className="data-table">
            <thead>
              <tr>
                <th>Label</th>
                <th>Client ID</th>
                <th>Workspace</th>
                <th>DHIS2 base URL</th>
                <th>Status</th>
                <th>Authorities</th>
                <th>Last used</th>
                <th>Secret rotated</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {t.paged.map((c) => (
                <tr key={c.clientId}>
                  <td><strong>{c.label}</strong></td>
                  <td className="soft"><code>{c.clientId}</code></td>
                  <td className="soft">{c.workspaceId || "—"}</td>
                  <td className="soft"><code>{c.dhis2BaseUrl || "—"}</code></td>
                  <td>{statusPill(c.status)}</td>
                  <td className="soft">{c.allowedAuthorities?.length ?? 0}</td>
                  <td className="soft">{fmt(c.lastUsedAt)}</td>
                  <td className="soft">{fmt(c.secretLastRotatedAt)}</td>
                  <td>
                    <button
                      className="linkbtn"
                      onClick={() => void onTriggerBackfill(c)}
                      disabled={busyId === c.clientId || c.status !== "active"}
                      title="Queue a full historical metadata + data backfill. The DHIS2 app picks it up on its next heartbeat (≤60s)."
                    >
                      Trigger backfill
                    </button>
                    {" · "}
                    <button
                      className="linkbtn"
                      onClick={() => void onRotate(c)}
                      disabled={busyId === c.clientId || c.status !== "active"}
                    >
                      Rotate
                    </button>
                    {" · "}
                    <button
                      className="linkbtn"
                      onClick={() => void onRevoke(c)}
                      disabled={busyId === c.clientId || c.status !== "active"}
                    >
                      Revoke
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {createForm && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)",
            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50
          }}
          onClick={(e) => { if (e.target === e.currentTarget && !creating) setCreateForm(null); }}
        >
          <form
            className="card"
            style={{ width: "min(560px, 92vw)", maxHeight: "92vh", overflow: "auto", padding: 20 }}
            onSubmit={(e) => { e.preventDefault(); void submitCreate(); }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <h2 style={{ margin: 0, fontSize: 16 }}>Add DHIS2 app client</h2>
              <button type="button" className="btn btn-sm" onClick={() => !creating && setCreateForm(null)}>Close</button>
            </div>
            <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
              The embedded TallyStream DHIS2 app uses these credentials to call back to the platform
              API. The secret is shown <strong>once</strong> after creation — copy it before closing.
            </p>
            <div style={{ display: "grid", gap: 12, marginTop: 12 }}>
              <label>
                Workspace <span style={{ color: "#b91c1c" }}>*</span>
                <select
                  value={createForm.workspaceId}
                  onChange={(e) => setCreateForm({ ...createForm, workspaceId: e.target.value })}
                  required
                >
                  <option value="">Pick a workspace…</option>
                  {tenantWorkspaces.map((w) => {
                    const display = (w as { workspaceName?: string }).workspaceName ?? w.name ?? w.id;
                    return <option key={w.id} value={w.id}>{display} ({w.slug ?? w.id})</option>;
                  })}
                </select>
              </label>
              <label>
                Label <span style={{ color: "#b91c1c" }}>*</span>
                <input
                  value={createForm.label}
                  onChange={(e) => setCreateForm({ ...createForm, label: e.target.value })}
                  placeholder="e.g. Production embedded app"
                  required
                />
              </label>
              <label>
                Description
                <input
                  value={createForm.description}
                  onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                  placeholder="What this client is for (optional)"
                />
              </label>
              <label>
                DHIS2 base URL
                <input
                  type="url"
                  value={createForm.dhis2BaseUrl}
                  onChange={(e) => setCreateForm({ ...createForm, dhis2BaseUrl: e.target.value })}
                  placeholder="https://dhis2.example.org"
                />
                <small className="muted" style={{ fontSize: 11 }}>
                  Optional — pin the client to a single DHIS2 instance.
                </small>
              </label>
              <label>
                Allowed authorities (comma-separated)
                <input
                  value={createForm.allowedAuthorities}
                  onChange={(e) => setCreateForm({ ...createForm, allowedAuthorities: e.target.value })}
                  placeholder="F_TRACKED_ENTITY_DATAVALUE_ADD, F_DATAVALUE_ADD, …"
                />
                <small className="muted" style={{ fontSize: 11 }}>
                  Optional — restricts which DHIS2 authorities the platform will accept on behalf of
                  this client.
                </small>
              </label>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
              <button type="button" className="btn" disabled={creating} onClick={() => setCreateForm(null)}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={creating}>
                {creating ? "Creating…" : "Create app client"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
